/**
 * API de listagem de questões (Question Bank).
 * Integração com v1/Question/pagination.
 */

export interface FetchQuestionsParams {
  page: number;
  limit: number;
  /** Incluir questões inativas (default: false) */
  includeInactive?: boolean;
  /** Título da questão (query: Title) */
  title?: string;
  /** Nível de dificuldade: 0=Low, 1=Medium, 2=High (query: DifficultyLevel) */
  difficultyLevel?: number;
  /** UUID da categoria de curso (query: CourseCategoryId) */
  courseCategoryId?: string;
  /** UUID do curso — filtra perguntas vinculadas ao curso (query: CourseId) */
  courseId?: string;
}

export interface QuestionListItem {
  id: string;
  question?: string;
  difficulty?: string;
  category?: string;
  linkedCourses?: string | string[];
  usage?: number;
  [key: string]: unknown;
}

export interface FetchQuestionsResponse {
  data: QuestionListItem[];
  total: number;
}

export type FetchQuestionsFn = (
  params: FetchQuestionsParams
) => Promise<FetchQuestionsResponse>;

/** Nível de dificuldade retornado pela API (objeto com id e name) */
export interface QuestionDifficultyLevel {
  id: number;
  name: string;
}

/** Item retornado por GET v1/Question/pagination (resposta da API Feraah) */
export interface QuestionPaginationItem {
  questionId: string;
  /**
   * Formato antigo da API: `title`.
   * Formato novo da API: `translations[]` (mantivemos para compatibilidade).
   */
  title?: string;
  translations?: { languageCode: string; title: string }[];
  difficultyLevel?: QuestionDifficultyLevel;
  courseCategoryName?: string;
  linkedCourses?: string[];
  usageQty?: number;
  [key: string]: unknown;
}

/** Resposta bruta de GET v1/Question/pagination */
export interface QuestionPaginationResponse {
  questions?: QuestionPaginationItem[];
  data?: QuestionPaginationItem[];
  totalRecords?: number;
  total?: number;
  totalPages?: number;
  pageNumber?: number;
  pageSize?: number;
}

/** Tradução retornada por GET v1/Question/{id} (translations da questão) */
export interface QuestionDetailTranslation {
  languageCode: string;
  title: string;
  explanation?: string;
}

/** Tradução de opção retornada por GET v1/Question/{id} (questionOptions[].translations) */
export interface QuestionOptionTranslation {
  languageCode: string;
  description: string;
}

/** Opção retornada por GET v1/Question/{id} (questionOptions[]) */
export interface QuestionOptionDetail {
  id: string;
  isCorrect: boolean;
  translations: QuestionOptionTranslation[];
}

/** Resposta de GET v1/Question/{id} (detalhes da questão para edição) */
export interface QuestionDetail {
  id: string;
  courseCategoryId: string;
  difficultyLevel: number;
  difficultyLevelName?: string;
  isActive: boolean;
  translations: QuestionDetailTranslation[];
  questionOptions: QuestionOptionDetail[];
  linkedCourseIds?: string[];
}

/** Cliente mínimo necessário (get). Compatível com ApiClient do core. */
export interface QuestionsListApiClient {
  get<T>(url: string): Promise<T>;
}

/** Cliente para criação de questão (post). Pode ser o mesmo que QuestionsListApiClient se tiver post. */
export interface QuestionsCreateApiClient {
  post<T>(url: string, body?: unknown): Promise<T>;
}

/** Cliente para exclusão de questão (delete). */
export interface QuestionsDeleteApiClient {
  delete<T>(url: string): Promise<T>;
}

/** Cliente para atualização de questão (put). */
export interface QuestionsUpdateApiClient {
  put<T>(url: string, data?: unknown): Promise<T>;
}

/** Payload para criação de questão (múltipla escolha). Alinhado ao formulário e ao backend. */
export interface CreateQuestionRequest {
  /** Traduções do texto da questão (en-US, es-ES) */
  translations: { languageCode: string; title: string }[];
  /** Nível de dificuldade: 0=Low, 1=Medium, 2=High */
  difficultyLevel: number;
  /** UUID da categoria de curso */
  courseCategoryId: string;
  /** IDs dos cursos vinculados (opcional) */
  linkedCourseIds?: string[];
  /** Opções de resposta com traduções (2 a 5 itens) */
  options: { languageCode: string; text: string }[][];
  /** Índice da opção correta (0-based) */
  correctOptionIndex: number;
  /** Explicação com traduções (opcional) */
  explanation?: { languageCode: string; text: string }[];
}

export interface CreateQuestionResponse {
  id: string;
}

const QUESTION_PAGINATION_PATH = 'v1/Question/pagination';
const QUESTION_PATH = 'v1/Question';

function mapPaginationItemToQuestion(item: QuestionPaginationItem): QuestionListItem {
  // A API pode retornar `title` (antigo) ou `translations[].title` (novo).
  const question = item.title ?? item.translations?.[0]?.title ?? '';
  const difficulty = item.difficultyLevel?.name;
  const category = item.courseCategoryName;
  const linkedCourses = item.linkedCourses ?? [];
  const usage = item.usageQty ?? 0;

  return {
    id: item.questionId,
    question,
    translations: item.translations,
    difficulty,
    category,
    linkedCourses: linkedCourses.length > 0 ? linkedCourses : undefined,
    usage,
  };
}

function normalizePaginationResponse(
  raw: QuestionPaginationResponse
): FetchQuestionsResponse {
  const items = raw.questions ?? raw.data ?? [];
  const total = raw.totalRecords ?? raw.total ?? 0;
  return {
    data: items.map(mapPaginationItemToQuestion),
    total,
  };
}

/** Body do PUT v1/Question/{id} (questionOptions incluem id de cada opção). */
export interface UpdateQuestionBody {
  courseCategoryId: string;
  difficultyLevel: number;
  translations: { languageCode: string; title: string; explanation: string }[];
  questionOptions: {
    id: string;
    isCorrect: boolean;
    translations: { languageCode: string; description: string }[];
  }[];
  linkedCourseIds?: string[];
}

/** Cliente com get (listagem), post (criação), put (atualização) e delete (exclusão). Usado por createQuestionsApi. */
export type QuestionsApiClient = QuestionsListApiClient &
  QuestionsCreateApiClient &
  QuestionsDeleteApiClient &
  QuestionsUpdateApiClient;

/** Body esperado pelo POST v1/Question (contrato da API Feraah). */
interface CreateQuestionApiBody {
  courseCategoryId: string;
  difficultyLevel: number;
  translations: { languageCode: string; title: string; explanation: string }[];
  questionOptions: {
    isCorrect: boolean;
    translations: { languageCode: string; description: string }[];
  }[];
  linkedCourseIds?: string[];
}

function buildCreateQuestionBody(request: CreateQuestionRequest): CreateQuestionApiBody {
  const translations = request.translations.map(t => ({
    languageCode: t.languageCode,
    title: t.title,
    explanation:
      request.explanation?.find(e => e.languageCode === t.languageCode)?.text ?? '',
  }));

  const questionOptions = request.options.map((opt, i) => ({
    isCorrect: i === request.correctOptionIndex,
    translations: opt.map(({ languageCode, text }) => ({
      languageCode,
      description: text,
    })),
  }));

  const body: CreateQuestionApiBody = {
    courseCategoryId: request.courseCategoryId,
    difficultyLevel: request.difficultyLevel,
    translations,
    questionOptions,
  };
  if (request.linkedCourseIds?.length) {
    body.linkedCourseIds = request.linkedCourseIds;
  }
  return body;
}

/**
 * Cria o cliente de API de questões (listagem e criação).
 * Recebe o api do app (b2b/b2b2c) e retorna { fetchQuestions, createQuestion }.
 */
export function createQuestionsApi(api: QuestionsApiClient) {
  return {
    /**
     * Lista questões com paginação (GET v1/Question/pagination).
     * Requer Authorization. Retorno compatível com o componente QuestionBank do shared.
     */
    fetchQuestions(params: FetchQuestionsParams): Promise<FetchQuestionsResponse> {
      const {
        page,
        limit,
        includeInactive = false,
        title,
        difficultyLevel,
        courseCategoryId,
        courseId,
      } = params;
      const searchParams = new URLSearchParams();
      searchParams.set('pageNumber', String(page));
      searchParams.set('pageSize', String(limit));
      searchParams.set('includeInactive', String(includeInactive));
      if (title?.trim()) searchParams.set('Title', title.trim());
      if (difficultyLevel !== undefined && difficultyLevel !== null)
        searchParams.set('DifficultyLevel', String(difficultyLevel));
      if (courseCategoryId?.trim())
        searchParams.set('CourseCategoryId', courseCategoryId.trim());
      if (courseId?.trim()) searchParams.set('CourseId', courseId.trim());

      const query = searchParams.toString();
      const url = `/${QUESTION_PAGINATION_PATH}?${query}`;

      return api
        .get<QuestionPaginationResponse>(url)
        .then(normalizePaginationResponse)
        .catch(err => {
          // O UI atualmente não mostra o motivo quando falha; log ajuda a identificar
          // se é 401/403, erro de query, ou formato inesperado da resposta.
          // eslint-disable-next-line no-console
          console.error('Failed to fetch questions pagination:', {
            url,
            error: err,
          });
          return { data: [], total: 0 };
        });
    },

    /**
     * Consulta detalhes de uma questão (GET v1/Question/{id}). Requer Authorization.
     * Usado para preencher o formulário na edição.
     */
    getQuestion(questionId: string): Promise<QuestionDetail> {
      const url = `/${QUESTION_PATH}/${encodeURIComponent(questionId)}`;
      return api.get<QuestionDetail>(url);
    },

    /**
     * Cria uma questão (POST v1/Question).
     * Requer Authorization. Body conforme CreateQuestionRequest.
     */
    createQuestion(request: CreateQuestionRequest): Promise<CreateQuestionResponse> {
      const url = `/${QUESTION_PATH}`;
      const body = buildCreateQuestionBody(request);
      return api.post<CreateQuestionResponse>(url, body);
    },

    /**
     * Atualiza uma questão (PUT v1/Question/{id}).
     * Requer Authorization. Body deve incluir id em cada questionOption.
     */
    updateQuestion(questionId: string, body: UpdateQuestionBody): Promise<void> {
      const url = `/${QUESTION_PATH}/${encodeURIComponent(questionId)}`;
      return api.put<unknown>(url, body) as Promise<void>;
    },

    /**
     * Remove uma questão (DELETE v1/Question/{id}).
     * Requer Authorization.
     */
    deleteQuestion(questionId: string): Promise<void> {
      const url = `/${QUESTION_PATH}/${encodeURIComponent(questionId)}`;
      return api.delete<unknown>(url) as Promise<void>;
    },
  };
}

export type QuestionsApi = ReturnType<typeof createQuestionsApi>;
